/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ContractBlockDto } from '../models/contract-block-dto';
import { contractCheckLanguageIsSupported } from '../fn/contract/contract-check-language-is-supported';
import { ContractCheckLanguageIsSupported$Params } from '../fn/contract/contract-check-language-is-supported';
import { ContractDto } from '../models/contract-dto';
import { contractGetCurrent } from '../fn/contract/contract-get-current';
import { ContractGetCurrent$Params } from '../fn/contract/contract-get-current';
import { contractIsContractBlocked } from '../fn/contract/contract-is-contract-blocked';
import { ContractIsContractBlocked$Params } from '../fn/contract/contract-is-contract-blocked';
import { ContractLanguageSupportedDto } from '../models/contract-language-supported-dto';
import { contractValidateAdmissionDate } from '../fn/contract/contract-validate-admission-date';
import { ContractValidateAdmissionDate$Params } from '../fn/contract/contract-validate-admission-date';
import { ValidationDto } from '../models/validation-dto';

@Injectable({ providedIn: 'root' })
export class ContractService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `contractGetCurrent()` */
  static readonly ContractGetCurrentPath = '/v1/Contract/GetCurrent';

  /**
   * Get the contractDto of current logged patient,   Permissions [AuthenticatedUser] ,   Restricted to [KIOSK_HOME|KIOSK_ONLY].
   *
   * It will return the contract matching with User cardNumber and Insurer
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `contractGetCurrent()` instead.
   *
   * This method doesn't expect any request body.
   */
  contractGetCurrent$Response(params?: ContractGetCurrent$Params, context?: HttpContext): Observable<StrictHttpResponse<ContractDto>> {
    return contractGetCurrent(this.http, this.rootUrl, params, context);
  }

  /**
   * Get the contractDto of current logged patient,   Permissions [AuthenticatedUser] ,   Restricted to [KIOSK_HOME|KIOSK_ONLY].
   *
   * It will return the contract matching with User cardNumber and Insurer
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `contractGetCurrent$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  contractGetCurrent(params?: ContractGetCurrent$Params, context?: HttpContext): Observable<ContractDto> {
    return this.contractGetCurrent$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContractDto>): ContractDto => r.body)
    );
  }

  /** Path part for operation `contractValidateAdmissionDate()` */
  static readonly ContractValidateAdmissionDatePath = '/v1/Contract/ValidateAdmissionDate';

  /**
   * Check if profile/contract is expired and Check if admission planned date is in valid range,   Permissions [AuthenticatedUser] ,   Restricted to [KIOSK_HOME].
   *
   * Object validation contract containing if it's 'OK' or not, and the reason why
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `contractValidateAdmissionDate()` instead.
   *
   * This method doesn't expect any request body.
   */
  contractValidateAdmissionDate$Response(params?: ContractValidateAdmissionDate$Params, context?: HttpContext): Observable<StrictHttpResponse<ValidationDto>> {
    return contractValidateAdmissionDate(this.http, this.rootUrl, params, context);
  }

  /**
   * Check if profile/contract is expired and Check if admission planned date is in valid range,   Permissions [AuthenticatedUser] ,   Restricted to [KIOSK_HOME].
   *
   * Object validation contract containing if it's 'OK' or not, and the reason why
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `contractValidateAdmissionDate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  contractValidateAdmissionDate(params?: ContractValidateAdmissionDate$Params, context?: HttpContext): Observable<ValidationDto> {
    return this.contractValidateAdmissionDate$Response(params, context).pipe(
      map((r: StrictHttpResponse<ValidationDto>): ValidationDto => r.body)
    );
  }

  /** Path part for operation `contractIsContractBlocked()` */
  static readonly ContractIsContractBlockedPath = '/v1/Contract/IsContractBlocked';

  /**
   * Get contract if it's contained in ContractBlocked list,   Permissions [AuthenticatedUser] ,   Restricted to [KIOSK_HOME].
   *
   * Contract blocked is returned if it's contained in ContractBlocked list
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `contractIsContractBlocked()` instead.
   *
   * This method doesn't expect any request body.
   */
  contractIsContractBlocked$Response(params?: ContractIsContractBlocked$Params, context?: HttpContext): Observable<StrictHttpResponse<ContractBlockDto>> {
    return contractIsContractBlocked(this.http, this.rootUrl, params, context);
  }

  /**
   * Get contract if it's contained in ContractBlocked list,   Permissions [AuthenticatedUser] ,   Restricted to [KIOSK_HOME].
   *
   * Contract blocked is returned if it's contained in ContractBlocked list
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `contractIsContractBlocked$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  contractIsContractBlocked(params?: ContractIsContractBlocked$Params, context?: HttpContext): Observable<ContractBlockDto> {
    return this.contractIsContractBlocked$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContractBlockDto>): ContractBlockDto => r.body)
    );
  }

  /** Path part for operation `contractCheckLanguageIsSupported()` */
  static readonly ContractCheckLanguageIsSupportedPath = '/v1/Contract/CheckLanguageIsSupported';

  /**
   * Check selected language is supported by questionnaire,   Permissions [AuthenticatedUser] ,   Restricted to [KIOSK_HOME].
   *
   * ContractLanguageSupported object containing if it's supported or not, the reason why and the selected language
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `contractCheckLanguageIsSupported()` instead.
   *
   * This method doesn't expect any request body.
   */
  contractCheckLanguageIsSupported$Response(params?: ContractCheckLanguageIsSupported$Params, context?: HttpContext): Observable<StrictHttpResponse<ContractLanguageSupportedDto>> {
    return contractCheckLanguageIsSupported(this.http, this.rootUrl, params, context);
  }

  /**
   * Check selected language is supported by questionnaire,   Permissions [AuthenticatedUser] ,   Restricted to [KIOSK_HOME].
   *
   * ContractLanguageSupported object containing if it's supported or not, the reason why and the selected language
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `contractCheckLanguageIsSupported$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  contractCheckLanguageIsSupported(params?: ContractCheckLanguageIsSupported$Params, context?: HttpContext): Observable<ContractLanguageSupportedDto> {
    return this.contractCheckLanguageIsSupported$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContractLanguageSupportedDto>): ContractLanguageSupportedDto => r.body)
    );
  }

}
